import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { NAV, UserIcon } from './userDashboard/theme';
import { useTheme } from './ThemeContext';
import { useAuth } from '../context/AuthContext';
import useResponsive from './userDashboard/ResponsiveHook'; // ✅ Import du hook responsive
import candidateService from '../services/candidateApi';


const logoImg = process.env.PUBLIC_URL + '/702844604_2344407339420357_5425605054250800936_n.jpg';


export default function Sidebar({ isOpen, onClose }) { // ✅ Ajout des props de contrôle
  const { C } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useResponsive(); // ✅ Détection du mode mobile/tablette

  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const loadProfileData = async () => {
      try {
        const data = await candidateService.getProfile();
        setProfile(data);
      } catch (err) {
        console.error("Erreur lors du chargement du profil dans la Sidebar", err);
      }
    };

    if (user) {
      loadProfileData();
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/'); // ✅ Redirection modifiée vers la Landing Page plutôt que /login
  };

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return (profile.first_name[0] + profile.last_name[0]).toUpperCase();
    }
    if (user?.email) return user.email.substring(0, 2).toUpperCase();
    return 'U';
  };

  const getDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  // ── Styles Dynamiques Réactifs ─────────────────────────
  const sidebarStyle = {
    width: 220, 
    minHeight: "100vh",
    background: C.bgCard,
    borderRight: `1px solid ${C.border}`,
    display: "flex", 
    flexDirection: "column",
    position: "fixed", 
    top: 0, 
    bottom: 0,
    zIndex: 100, // Augmenté pour passer au-dessus de la Topbar sur mobile
    overflow: "hidden",
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)", // Animation fluide
    
    // ✅ Comportement sur Mobile / Tablette vs Desktop
    left: 0,
    transform: isMobile ? (isOpen ? "translateX(0)" : "translateX(-100%)") : "translateX(0)",
    boxShadow: isMobile && isOpen ? "10px 0 30px rgba(0,0,0,0.3)" : "none",
  };

  return (
    <>
      {/* ✅ Arrière-plan sombre (Backdrop) cliquable uniquement sur mobile */}
      {isMobile && isOpen && (
        <div 
          onClick={onClose}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(3px)",
            zIndex: 99,
            transition: "opacity 0.3s ease",
          }}
        />
      )}

      <aside style={sidebarStyle}>
        {/* Ligne décorative à gauche */}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: 2,
          background: `linear-gradient(180deg, transparent, ${C.accent} 35%, ${C.pink} 65%, transparent)`,
          opacity: 0.75,
        }} />

        {/* Logo Section */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "22px 20px 18px",
          borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={logoImg} alt="Logo" style={{ width: 32, height: 32, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, fontWeight: 700, color: C.textPrimary }}>
              <span style={{ color: C.accent }}>Skill</span>Hunt
            </span>
          </div>

          {/* ✅ Petit bouton Fermer "✕" visible uniquement sur mobile */}
          {isMobile && (
            <button 
              onClick={onClose}
              style={{
                background: "none", border: "none", color: C.textMuted,
                fontSize: 16, cursor: "pointer", display: "flex", 
                alignItems: "center", justifyContent: "center", padding: 4
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Navigation Links */}
        <nav style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          {NAV.map(({ section, items }) => (
            <div key={section}>
              <div style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: "1.8px",
                textTransform: "uppercase", color: C.textMuted,
                padding: "18px 20px 8px",
              }}>{section}</div>

              {items.map(({ id, label, icon }) => {
                const path = id === "dashboard" ? "/dashboard" : `/dashboard/${id}`;
                return (
                  <NavLink 
                    key={id} 
                    to={path}
                    end={id === "dashboard"}
                    onClick={isMobile ? onClose : undefined} // ✅ Ferme le tiroir après un clic sur mobile
                    style={({ isActive }) => ({
                      display: "flex", alignItems: "center", gap: 11,
                      padding: "10px 20px", width: "100%",
                      background: isActive ? C.accentDim : "transparent",
                      textDecoration: "none",
                      borderLeft: `2px solid ${isActive ? C.accent : "transparent"}`,
                      color: isActive ? C.accent : C.textSecond,
                      fontSize: 13.5, fontWeight: isActive ? 600 : 500,
                      cursor: "pointer", textAlign: "left", transition: "all 0.18s",
                    })}
                  >
                    {({ isActive }) => (
                      <>
                        <span style={{ opacity: isActive ? 1 : 0.7, flexShrink: 0 }}>{icon}</span>
                        <span style={{ flex: 1 }}>{label}</span>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Profil Link */}
        <NavLink 
          to="/dashboard/profile"
          onClick={isMobile ? onClose : undefined} // ✅ Ferme le tiroir au clic sur mobile
          style={({ isActive }) => ({
            display: "flex", alignItems: "center", gap: 11,
            padding: "10px 20px", width: "100%",
            background: isActive ? C.accentDim : "transparent",
            textDecoration: "none",
            borderLeft: `2px solid ${isActive ? C.accent : "transparent"}`,
            color: isActive ? C.accent : C.textSecond,
            fontSize: 13.5, fontWeight: isActive ? 600 : 500, cursor: "pointer", textAlign: "left", marginBottom: 4,
            transition: "all 0.18s",
          })}
        >
          <span style={{ opacity: 0.7, flexShrink: 0 }}><UserIcon /></span>
          Profile
        </NavLink>

        {/* User Section / Logout */}
        <div 
          onClick={handleLogout}
          style={{
            padding: "14px 20px", borderTop: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
            transition: "background 0.18s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        >
          <div style={{
            width: 34, height: 34, borderRadius: 8, flexShrink: 0,
            background: C.gradMain,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Space Mono', monospace", fontSize: 13, fontWeight: 700, color: "#fff",
          }}>
            {getInitials()}
          </div>
          <div style={{ overflow: "hidden", flex: 1 }}>
            <div style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: C.textPrimary, 
              whiteSpace: "nowrap", 
              overflow: "hidden", 
              textOverflow: "ellipsis",
              textTransform: "capitalize"
            }}>
              {getDisplayName()}
            </div>
            <div style={{ fontSize: 11, color: C.accent, fontFamily: "'Space Mono', monospace" }}>
              Click to logout
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}